import { ClientResponseError, type RecordModel } from 'pocketbase';

import {
  type EmailOtpType,
  getPB,
  getSupabaseUrl,
  getUser,
  newLegacyId,
  pbDate,
  randomHex,
} from './client';
import { fileContentStats } from './file-content-stats';

export type { EmailOtpType, Session, User } from './client';
export {
  ensureFreshSession,
  getPB,
  getPBUrl,
  getSession,
  getSupabase,
  getSupabaseUrl,
  getUser,
  isPbConfigured,
  isSupabaseConfigured,
  newLegacyId,
  onAuthStateChange,
} from './client';
export type { FileContentStats } from './file-content-stats';
export { fileContentStats } from './file-content-stats';

// ---------------------------------------------------------------------------
// PocketBase id helpers — app-facing ids prefer legacy_id (old Supabase UUID)
// ---------------------------------------------------------------------------

function publicId(record: RecordModel | Record<string, unknown>): string {
  const legacy = record.legacy_id;
  if (typeof legacy === 'string' && legacy) return legacy;
  return String(record.id);
}

async function resolveWorkspaceRecord(
  publicWorkspaceId: string,
): Promise<RecordModel> {
  const pb = getPB();
  return pb
    .collection('workspaces')
    .getFirstListItem(
      pb.filter('legacy_id = {:id} || id = {:id}', { id: publicWorkspaceId }),
    );
}

/** Cache public workspace id → PocketBase record id (shared across file/API calls). */
const workspacePbIdCache = new Map<string, string>();

async function resolveWorkspacePbId(
  publicWorkspaceId: string,
): Promise<string> {
  const cached = workspacePbIdCache.get(publicWorkspaceId);
  if (cached) return cached;
  const row = await resolveWorkspaceRecord(publicWorkspaceId);
  workspacePbIdCache.set(publicWorkspaceId, row.id);
  // Also cache under the PB id itself so callers that already have it skip a round-trip.
  workspacePbIdCache.set(row.id, row.id);
  const legacy = row.legacy_id;
  if (typeof legacy === 'string' && legacy) {
    workspacePbIdCache.set(legacy, row.id);
  }
  return row.id;
}

function mapWorkspace(r: RecordModel): WorkspaceRow {
  const owner =
    (r.expand as { owner?: { id: string } } | undefined)?.owner?.id ||
    (typeof r.owner === 'string' ? r.owner : '') ||
    (typeof r.owner_id === 'string' ? r.owner_id : '');
  return {
    id: publicId(r),
    title: String(r.title ?? ''),
    owner_id: owner,
    created_at: pbDate(r.created ?? r.created_at),
    updated_at: pbDate(r.updated ?? r.updated_at),
  };
}

function mapMember(r: RecordModel, workspacePublicId: string): WorkspaceMember {
  const userId =
    (r.expand as { user?: { id: string } } | undefined)?.user?.id ||
    (typeof r.user === 'string' ? r.user : '') ||
    (typeof r.user_id === 'string' ? r.user_id : '');
  return {
    workspace_id: workspacePublicId,
    user_id: userId,
    role: (r.role as WorkspaceMember['role']) || 'viewer',
    project_wide: Boolean(r.project_wide),
    joined_at: pbDate(r.joined_at ?? r.created),
  };
}

function mapFile(r: RecordModel, workspacePublicId?: string): WorkspaceFileRow {
  const ws =
    workspacePublicId ||
    (typeof r.workspace_legacy_id === 'string' ? r.workspace_legacy_id : '') ||
    (typeof r.workspace_id === 'string' ? r.workspace_id : '') ||
    (typeof r.workspace === 'string' ? r.workspace : '');
  // PocketBase `editor` fields coerce JSON null to "" on write. Binary rows
  // still carry storage_path; treat them as content-less so readers download
  // the asset instead of materializing an empty File (broken <img> alt text).
  const storagePath =
    typeof r.storage_path === 'string' && r.storage_path
      ? r.storage_path
      : null;
  const rawContent = typeof r.content === 'string' ? r.content : null;
  return {
    id: publicId(r),
    workspace_id: ws,
    path: String(r.path ?? ''),
    content: storagePath ? null : rawContent,
    storage_path: storagePath,
    content_type: String(r.content_type ?? 'text/markdown'),
    is_private: Boolean(r.is_private),
    byte_size: Number(r.byte_size ?? 0),
    content_hash: (r.content_hash as string | null) ?? null,
    word_count: (r.word_count as number | null) ?? null,
    char_count: (r.char_count as number | null) ?? null,
    char_count_no_spaces: (r.char_count_no_spaces as number | null) ?? null,
    ctime: pbDate(r.ctime ?? r.created),
    mtime: pbDate(r.mtime ?? r.updated),
  };
}

function mapInvite(
  r: RecordModel,
  workspacePublicId?: string,
): WorkspaceInvite {
  const ws =
    workspacePublicId ||
    (typeof r.workspace_legacy_id === 'string' ? r.workspace_legacy_id : '') ||
    (typeof r.workspace_id === 'string' ? r.workspace_id : '') ||
    (typeof r.workspace === 'string' ? r.workspace : '');
  return {
    id: publicId(r),
    code: String(r.code ?? ''),
    workspace_id: ws,
    created_by:
      (typeof r.created_by === 'string' ? r.created_by : '') ||
      (typeof r.created_by_id === 'string' ? r.created_by_id : ''),
    role: (r.role as WorkspaceInvite['role']) || 'editor',
    scope: (r.scope as WorkspaceInvite['scope']) || 'project',
    item_path: (r.item_path as string | null) ?? null,
    expires_at: r.expires_at ? pbDate(r.expires_at) : null,
    revoked_at: r.revoked_at ? pbDate(r.revoked_at) : null,
    max_uses: Number(r.max_uses ?? 0),
    use_count: Number(r.use_count ?? 0),
    created_at: pbDate(r.created ?? r.created_at),
  };
}

function mapShareLink(
  r: RecordModel,
  workspacePublicId?: string,
): WorkspaceShareLink {
  const ws =
    workspacePublicId ||
    (typeof r.workspace_legacy_id === 'string' ? r.workspace_legacy_id : '') ||
    (typeof r.workspace_id === 'string' ? r.workspace_id : '') ||
    (typeof r.workspace === 'string' ? r.workspace : '');
  return {
    id: publicId(r),
    token: String(r.token ?? ''),
    workspace_id: ws,
    item_path: (r.item_path as string | null) ?? null,
    title_cache: String(r.title_cache ?? ''),
    allow_copy: Boolean(r.allow_copy),
    expires_at: r.expires_at ? pbDate(r.expires_at) : null,
    revoked_at: r.revoked_at ? pbDate(r.revoked_at) : null,
    created_at: pbDate(r.created ?? r.created_at),
    refreshed_at: pbDate(r.refreshed_at ?? r.updated ?? r.created),
  };
}

function titleFromPath(path: string): string {
  const base = path.split('/').pop() || path;
  return base.replace(/\.md$/i, '');
}

async function findFileRecord(
  workspacePbId: string,
  path: string,
): Promise<RecordModel | null> {
  const pb = getPB();
  try {
    return await pb.collection('workspace_files').getFirstListItem(
      pb.filter('workspace = {:ws} && path = {:path}', {
        ws: workspacePbId,
        path,
      }),
    );
  } catch {
    return null;
  }
}

export type WorkspaceRow = {
  id: string;
  title: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
};

export type WorkspaceMember = {
  workspace_id: string;
  user_id: string;
  role: 'owner' | 'editor' | 'viewer';
  project_wide: boolean;
  joined_at: string;
};

export type WorkspaceItemGrant = {
  id: string;
  workspace_id: string;
  user_id: string;
  item_path: string;
  role: 'editor' | 'viewer';
  granted_at: string;
};

export type WorkspaceFileRow = {
  id: string;
  workspace_id: string;
  path: string;
  content: string | null;
  storage_path: string | null;
  content_type: string;
  is_private: boolean;
  byte_size: number;
  content_hash: string | null;
  word_count: number | null;
  char_count: number | null;
  char_count_no_spaces: number | null;
  ctime: string;
  mtime: string;
};

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function signInWithPassword(email: string, password: string) {
  const pb = getPB();
  const auth = await pb
    .collection('users')
    .authWithPassword(email.trim(), password);
  return {
    user: {
      id: auth.record.id,
      email: auth.record.email,
      user_metadata: {
        display_name: auth.record.display_name as string | undefined,
      },
    },
    session: {
      user: {
        id: auth.record.id,
        email: auth.record.email,
        user_metadata: {
          display_name: auth.record.display_name as string | undefined,
        },
      },
      access_token: auth.token,
    },
  };
}

export async function signUpWithPassword(
  email: string,
  password: string,
  displayName?: string,
) {
  const pb = getPB();
  const e = email.trim().toLowerCase();
  const display_name = displayName?.trim() || e.split('@')[0] || 'User';
  const created = await pb.collection('users').create({
    email: e,
    password,
    passwordConfirm: password,
    emailVisibility: true,
    display_name,
  });
  const auth = await pb.collection('users').authWithPassword(e, password);
  try {
    await pb.collection('profiles').create({
      legacy_id: created.id,
      user: created.id,
      email: e,
      display_name,
    });
  } catch {
    /* profile may already exist */
  }
  return {
    user: {
      id: auth.record.id,
      email: auth.record.email,
      user_metadata: { display_name },
    },
    session: {
      user: {
        id: auth.record.id,
        email: auth.record.email,
        user_metadata: { display_name },
      },
      access_token: auth.token,
    },
  };
}

export async function signInWithMagicLink(_email: string) {
  throw new Error(
    'Magic-link login is not available on PocketBase yet. Use email + password, or request a password reset.',
  );
}

export async function signOut() {
  getPB().authStore.clear();
}

export async function exchangeCodeForSession(_code: string) {
  throw new Error(
    'PKCE code exchange is not used with PocketBase. Open the password-reset link from your email, or sign in with email + password.',
  );
}

/** Email-link confirm / recovery — PocketBase uses confirmPasswordReset tokens. */
export async function verifyEmailOtp(params: {
  tokenHash: string;
  type: EmailOtpType;
}) {
  if (params.type === 'recovery') {
    // Caller must follow up with confirmPasswordReset via updatePassword UI.
    // PocketBase recovery links carry a token in the URL, not a Supabase OTP.
    throw new Error(
      'Use the password reset form with the token from your email link (PocketBase confirmPasswordReset).',
    );
  }
  throw new Error(
    `Email OTP type "${params.type}" is not supported on PocketBase. Use email + password.`,
  );
}

export function isPkceVerifierMissingError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /pkce|code verifier/i.test(message);
}

export async function requestPasswordReset(email: string): Promise<void> {
  await getPB().collection('users').requestPasswordReset(email.trim());
}

export async function confirmPasswordReset(
  token: string,
  password: string,
  passwordConfirm = password,
): Promise<void> {
  await getPB()
    .collection('users')
    .confirmPasswordReset(token, password, passwordConfirm);
}

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

export async function createCloudWorkspace(
  title: string,
): Promise<WorkspaceRow> {
  const user = await getUser();
  if (!user) throw new Error('not authenticated');
  const trimmed = title.trim();
  if (!trimmed) throw new Error('title required');

  const pb = getPB();
  const legacy = newLegacyId();
  const ws = await pb.collection('workspaces').create({
    legacy_id: legacy,
    title: trimmed,
    owner: user.id,
  });
  await pb.collection('workspace_members').create({
    legacy_id: newLegacyId(),
    workspace: ws.id,
    user: user.id,
    role: 'owner',
    project_wide: true,
  });
  return mapWorkspace(ws);
}

function rethrowAuthDeath(error: unknown): never {
  if (
    error instanceof ClientResponseError &&
    (error.status === 401 || error.status === 403)
  ) {
    getPB().authStore.clear();
  }
  throw error;
}

export async function listMyWorkspaces(): Promise<WorkspaceRow[]> {
  const user = await getUser();
  if (!user) return [];
  const pb = getPB();
  try {
    const memberships = await pb.collection('workspace_members').getFullList({
      filter: pb.filter('user = {:uid}', { uid: user.id }),
    });
    if (memberships.length === 0) return [];
    const wsIds = [
      ...new Set(
        memberships.map((m) =>
          typeof m.workspace === 'string' ? m.workspace : '',
        ),
      ),
    ].filter(Boolean);
    if (wsIds.length === 0) return [];
    const filter = wsIds
      .map((id) => pb.filter('id = {:id}', { id }))
      .join(' || ');
    const rows = await pb.collection('workspaces').getFullList({ filter });
    return rows.map(mapWorkspace);
  } catch (error) {
    rethrowAuthDeath(error);
  }
}

export async function getMyMembership(
  workspaceId: string,
): Promise<WorkspaceMember | null> {
  const user = await getUser();
  if (!user) return null;
  const pb = getPB();
  let wsPbId: string;
  try {
    wsPbId = await resolveWorkspacePbId(workspaceId);
  } catch {
    return null;
  }
  try {
    const row = await pb.collection('workspace_members').getFirstListItem(
      pb.filter('workspace = {:ws} && user = {:uid}', {
        ws: wsPbId,
        uid: user.id,
      }),
    );
    return mapMember(row, workspaceId);
  } catch {
    return null;
  }
}

async function removeWorkspaceAssets(workspaceId: string): Promise<void> {
  const pb = getPB();
  let wsPbId: string;
  try {
    wsPbId = await resolveWorkspacePbId(workspaceId);
  } catch {
    return;
  }
  const assets = await pb.collection('note_assets').getFullList({
    filter: pb.filter('workspace = {:ws}', { ws: wsPbId }),
  });
  for (const asset of assets) {
    await pb.collection('note_assets').delete(asset.id);
  }
}

/**
 * Remove a cloud workspace for every member (owner) or leave it (non-owner).
 */
export async function deleteCloudWorkspace(workspaceId: string): Promise<void> {
  const user = await getUser();
  if (!user) throw new Error('not authenticated');

  const membership = await getMyMembership(workspaceId);
  if (!membership) return;

  const pb = getPB();
  const ws = await resolveWorkspaceRecord(workspaceId);

  if (membership.role === 'owner') {
    await removeWorkspaceAssets(workspaceId).catch((error: unknown) => {
      console.warn('failed to purge cloud workspace assets', error);
    });
    await pb.collection('workspaces').delete(ws.id);
    return;
  }

  const row = await pb.collection('workspace_members').getFirstListItem(
    pb.filter('workspace = {:ws} && user = {:uid}', {
      ws: ws.id,
      uid: user.id,
    }),
  );
  await pb.collection('workspace_members').delete(row.id);
}

export async function listMembers(
  workspaceId: string,
): Promise<WorkspaceMember[]> {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const rows = await pb.collection('workspace_members').getFullList({
    filter: pb.filter('workspace = {:ws}', { ws: wsPbId }),
  });
  return rows.map((r) => mapMember(r, workspaceId));
}

export type WorkspaceInvite = {
  id: string;
  code: string;
  workspace_id: string;
  created_by: string;
  role: 'editor' | 'viewer';
  scope: 'project' | 'item';
  item_path: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  max_uses: number;
  use_count: number;
  created_at: string;
};

export type WorkspaceShareLink = {
  id: string;
  token: string;
  workspace_id: string;
  item_path: string | null;
  title_cache: string;
  allow_copy: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  refreshed_at: string;
};

export type ProfileSocialLinkKey =
  | 'website'
  | 'github'
  | 'mastodon'
  | 'bluesky'
  | 'youtube'
  | 'email';

export const PROFILE_SOCIAL_LINK_KEYS: readonly ProfileSocialLinkKey[] = [
  'bluesky',
  'email',
  'github',
  'mastodon',
  'website',
  'youtube',
] as const;

export type ProfileSocialLinks = Partial<Record<ProfileSocialLinkKey, string>>;

export type ProfileRow = {
  id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  bio?: string | null;
  social_links?: ProfileSocialLinks | null;
};

function normalizeSocialLinks(value: unknown): ProfileSocialLinks {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const record = value as Record<string, unknown>;
  const links: ProfileSocialLinks = {};
  for (const key of PROFILE_SOCIAL_LINK_KEYS) {
    const raw = record[key];
    if (typeof raw === 'string' && raw.trim()) {
      links[key] = raw.trim();
    }
  }
  return links;
}

function mapProfile(r: RecordModel, fallbackUserId?: string): ProfileRow {
  const userId =
    (typeof r.user === 'string' ? r.user : '') || fallbackUserId || publicId(r);
  return {
    id: userId,
    display_name: (r.display_name as string | null) ?? null,
    email: (r.email as string | null) ?? null,
    avatar_url: (r.avatar_url as string | null) ?? null,
    bio: (r.bio as string | null) ?? null,
    social_links: normalizeSocialLinks(r.social_links),
  };
}

export async function fetchMyProfile(): Promise<ProfileRow | null> {
  const user = await getUser();
  if (!user) return null;
  const pb = getPB();
  try {
    const row = await pb
      .collection('profiles')
      .getFirstListItem(pb.filter('user = {:uid}', { uid: user.id }));
    return mapProfile(row, user.id);
  } catch {
    return {
      id: user.id,
      display_name:
        user.user_metadata?.display_name ?? user.email?.split('@')[0] ?? null,
      email: user.email ?? null,
      avatar_url: null,
      bio: null,
      social_links: {},
    };
  }
}

export async function updateMyProfile(patch: {
  display_name?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  social_links?: ProfileSocialLinks;
}): Promise<ProfileRow> {
  const user = await getUser();
  if (!user) throw new Error('Not signed in');
  const pb = getPB();

  const updates: Record<string, unknown> = {};
  if ('display_name' in patch) {
    updates.display_name = patch.display_name?.trim() || null;
  }
  if ('avatar_url' in patch) {
    updates.avatar_url = patch.avatar_url?.trim() || null;
  }
  if ('bio' in patch) {
    updates.bio = patch.bio?.trim() || null;
  }
  if ('social_links' in patch) {
    updates.social_links = normalizeSocialLinks(patch.social_links);
  }
  if (user.email) updates.email = user.email;

  try {
    const existing = await pb
      .collection('profiles')
      .getFirstListItem(pb.filter('user = {:uid}', { uid: user.id }));
    const row = await pb.collection('profiles').update(existing.id, updates);
    return mapProfile(row, user.id);
  } catch {
    const row = await pb.collection('profiles').create({
      legacy_id: user.id,
      user: user.id,
      email: user.email ?? '',
      display_name:
        ('display_name' in patch
          ? patch.display_name?.trim() || null
          : user.user_metadata?.display_name) ?? null,
      avatar_url:
        'avatar_url' in patch ? patch.avatar_url?.trim() || null : null,
      bio: 'bio' in patch ? patch.bio?.trim() || null : null,
      social_links:
        'social_links' in patch ? normalizeSocialLinks(patch.social_links) : {},
    });
    return mapProfile(row, user.id);
  }
}

export async function fetchProfilesByIds(
  ids: string[],
): Promise<Record<string, ProfileRow>> {
  if (ids.length === 0) return {};
  const pb = getPB();
  const unique = [...new Set(ids.filter(Boolean))];
  const filter = unique
    .map((id) => pb.filter('user = {:id} || legacy_id = {:id}', { id }))
    .join(' || ');
  const rows = await pb.collection('profiles').getFullList({ filter });
  const map: Record<string, ProfileRow> = {};
  for (const row of rows) {
    const profile = mapProfile(row);
    map[profile.id] = profile;
    const legacy = row.legacy_id;
    if (typeof legacy === 'string' && legacy) map[legacy] = profile;
  }
  return map;
}

export async function createInvite(params: {
  workspaceId: string;
  scope?: 'project' | 'item';
  itemPath?: string;
}): Promise<WorkspaceInvite> {
  const user = await getUser();
  if (!user) throw new Error('not authenticated');
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  const code = randomHex(8).toUpperCase();
  const row = await pb.collection('workspace_invites').create({
    legacy_id: newLegacyId(),
    code,
    workspace: wsPbId,
    created_by: user.id,
    role: 'editor',
    scope: params.scope ?? 'project',
    item_path: params.itemPath ?? null,
    max_uses: 0,
    use_count: 0,
  });
  return mapInvite(row, params.workspaceId);
}

export async function listInvites(
  workspaceId: string,
): Promise<WorkspaceInvite[]> {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const rows = await pb.collection('workspace_invites').getFullList({
    filter: pb.filter('workspace = {:ws} && revoked_at = ""', { ws: wsPbId }),
    sort: '-created',
  });
  // PB empty date may be '' or missing; also filter client-side.
  return rows
    .filter((r) => !r.revoked_at)
    .map((r) => mapInvite(r, workspaceId));
}

export async function revokeInvite(inviteId: string) {
  const pb = getPB();
  const row = await pb
    .collection('workspace_invites')
    .getFirstListItem(
      pb.filter('legacy_id = {:id} || id = {:id}', { id: inviteId }),
    );
  await pb.collection('workspace_invites').update(row.id, {
    revoked_at: new Date().toISOString(),
  });
}

export async function removeMember(workspaceId: string, userId: string) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const row = await pb.collection('workspace_members').getFirstListItem(
    pb.filter('workspace = {:ws} && user = {:uid}', {
      ws: wsPbId,
      uid: userId,
    }),
  );
  if (row.role === 'owner') {
    throw new Error('cannot remove owner');
  }
  await pb.collection('workspace_members').delete(row.id);
}

export async function redeemInvite(code: string) {
  const user = await getUser();
  if (!user) throw new Error('not authenticated');
  const pb = getPB();
  const invite = await pb
    .collection('workspace_invites')
    .getFirstListItem(pb.filter('code = {:code}', { code: code.trim() }));
  if (invite.revoked_at) throw new Error('invite revoked');
  if (invite.expires_at && new Date(pbDate(invite.expires_at)) < new Date()) {
    throw new Error('invite expired');
  }
  const maxUses = Number(invite.max_uses ?? 0);
  const useCount = Number(invite.use_count ?? 0);
  if (maxUses > 0 && useCount >= maxUses) {
    throw new Error('invite exhausted');
  }

  const wsPbId = String(invite.workspace);
  const ws = await pb.collection('workspaces').getOne(wsPbId);
  const workspacePublicId = publicId(ws);

  try {
    await pb.collection('workspace_members').getFirstListItem(
      pb.filter('workspace = {:ws} && user = {:uid}', {
        ws: wsPbId,
        uid: user.id,
      }),
    );
  } catch {
    await pb.collection('workspace_members').create({
      legacy_id: newLegacyId(),
      workspace: wsPbId,
      user: user.id,
      role: invite.role || 'editor',
      project_wide: invite.scope !== 'item',
    });
  }

  if (invite.scope === 'item' && invite.item_path) {
    try {
      await pb
        .collection('workspace_item_grants')
        .getFirstListItem(
          pb.filter(
            'workspace = {:ws} && user = {:uid} && item_path = {:path}',
            { ws: wsPbId, uid: user.id, path: invite.item_path },
          ),
        );
    } catch {
      await pb.collection('workspace_item_grants').create({
        legacy_id: newLegacyId(),
        workspace: wsPbId,
        user: user.id,
        item_path: invite.item_path,
        role: (invite.role as 'editor' | 'viewer') || 'viewer',
      });
    }
  }

  await pb.collection('workspace_invites').update(invite.id, {
    use_count: useCount + 1,
  });

  return {
    workspace_id: workspacePublicId,
    role: String(invite.role || 'editor'),
    scope: String(invite.scope || 'project'),
    item_path: (invite.item_path as string | null) ?? null,
  };
}

export async function grantItemAccess(params: {
  workspaceId: string;
  userId: string;
  itemPath: string;
  role?: 'editor' | 'viewer';
}) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  try {
    const existing = await pb
      .collection('workspace_item_grants')
      .getFirstListItem(
        pb.filter('workspace = {:ws} && user = {:uid} && item_path = {:path}', {
          ws: wsPbId,
          uid: params.userId,
          path: params.itemPath,
        }),
      );
    await pb.collection('workspace_item_grants').update(existing.id, {
      role: params.role ?? 'viewer',
    });
  } catch {
    await pb.collection('workspace_item_grants').create({
      legacy_id: newLegacyId(),
      workspace: wsPbId,
      user: params.userId,
      item_path: params.itemPath,
      role: params.role ?? 'viewer',
    });
  }
}

export async function revokeItemAccess(params: {
  workspaceId: string;
  userId: string;
  itemPath: string;
}) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  const row = await pb.collection('workspace_item_grants').getFirstListItem(
    pb.filter('workspace = {:ws} && user = {:uid} && item_path = {:path}', {
      ws: wsPbId,
      uid: params.userId,
      path: params.itemPath,
    }),
  );
  await pb.collection('workspace_item_grants').delete(row.id);
}

export async function listItemGrants(
  workspaceId: string,
): Promise<WorkspaceItemGrant[]> {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const rows = await pb.collection('workspace_item_grants').getFullList({
    filter: pb.filter('workspace = {:ws}', { ws: wsPbId }),
    sort: '-created',
  });
  return rows.map((r) => ({
    id: publicId(r),
    workspace_id: workspaceId,
    user_id:
      (typeof r.user === 'string' ? r.user : '') ||
      (typeof r.user_id === 'string' ? r.user_id : ''),
    item_path: String(r.item_path ?? ''),
    role: (r.role as 'editor' | 'viewer') || 'viewer',
    granted_at: pbDate(r.granted_at ?? r.created),
  }));
}

export async function setMemberRole(
  workspaceId: string,
  userId: string,
  role: 'editor' | 'viewer',
) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const row = await pb.collection('workspace_members').getFirstListItem(
    pb.filter('workspace = {:ws} && user = {:uid}', {
      ws: wsPbId,
      uid: userId,
    }),
  );
  if (row.role === 'owner') throw new Error('cannot change owner role');
  await pb.collection('workspace_members').update(row.id, { role });
}

export async function setMemberProjectWide(
  workspaceId: string,
  userId: string,
  projectWide: boolean,
) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const row = await pb.collection('workspace_members').getFirstListItem(
    pb.filter('workspace = {:ws} && user = {:uid}', {
      ws: wsPbId,
      uid: userId,
    }),
  );
  await pb
    .collection('workspace_members')
    .update(row.id, { project_wide: projectWide });
}

export async function setFilePrivate(
  workspaceId: string,
  path: string,
  isPrivate: boolean,
) {
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const row = await findFileRecord(wsPbId, path);
  if (!row) throw new Error('file not found');
  await getPB().collection('workspace_files').update(row.id, {
    is_private: isPrivate,
    mtime: new Date().toISOString(),
  });
}

export async function getFilePrivate(
  workspaceId: string,
  path: string,
): Promise<boolean> {
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const row = await findFileRecord(wsPbId, path);
  return Boolean(row?.is_private);
}

export async function setDirPrivate(
  workspaceId: string,
  dirPath: string,
  isPrivate: boolean,
) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const trimmed = dirPath.replace(/\/$/, '');
  try {
    const existing = await pb
      .collection('workspace_private_dirs')
      .getFirstListItem(
        pb.filter('workspace = {:ws} && dir_path = {:path}', {
          ws: wsPbId,
          path: trimmed,
        }),
      );
    if (!isPrivate) {
      await pb.collection('workspace_private_dirs').delete(existing.id);
    }
  } catch {
    if (isPrivate) {
      await pb.collection('workspace_private_dirs').create({
        legacy_id: newLegacyId(),
        workspace: wsPbId,
        dir_path: trimmed,
      });
    }
  }
}

export async function getDirPrivate(
  workspaceId: string,
  dirPath: string,
): Promise<boolean> {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const trimmed = dirPath.replace(/\/$/, '');
  try {
    await pb.collection('workspace_private_dirs').getFirstListItem(
      pb.filter('workspace = {:ws} && dir_path = {:path}', {
        ws: wsPbId,
        path: trimmed,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

export async function listPrivateFilePaths(
  workspaceId: string,
  abortSignal?: AbortSignal,
): Promise<string[]> {
  if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const rows = await pb.collection('workspace_files').getFullList({
    filter: pb.filter('workspace = {:ws} && is_private = true', {
      ws: wsPbId,
    }),
    fields: 'path',
  });
  if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
  return rows.map((r) => String(r.path ?? ''));
}

export async function listPrivateDirPaths(
  workspaceId: string,
  abortSignal?: AbortSignal,
): Promise<string[]> {
  if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const pb = getPB();
  try {
    const wsPbId = await resolveWorkspacePbId(workspaceId);
    const rows = await pb.collection('workspace_private_dirs').getFullList({
      filter: pb.filter('workspace = {:ws}', { ws: wsPbId }),
      fields: 'dir_path',
    });
    if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return rows.map((r) => String(r.dir_path ?? ''));
  } catch {
    // Owner-only collection — other members get an empty list.
    return [];
  }
}

export async function createShareLink(params: {
  workspaceId: string;
  itemPath: string;
  allowCopy?: boolean;
}): Promise<WorkspaceShareLink> {
  const user = await getUser();
  if (!user) throw new Error('not authenticated');
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  const itemPath = params.itemPath.trim();
  const file = await findFileRecord(wsPbId, itemPath);
  const markdown = (file?.content as string | null) ?? '';
  const title = file
    ? titleFromPath(String(file.path))
    : titleFromPath(itemPath);
  const token = randomHex(16);
  const now = new Date().toISOString();
  const row = await pb.collection('workspace_share_links').create({
    legacy_id: newLegacyId(),
    token,
    workspace: wsPbId,
    item_path: itemPath,
    created_by: user.id,
    markdown_cache: markdown,
    title_cache: title,
    allow_copy: params.allowCopy ?? true,
    refreshed_at: now,
  });

  // Allowlist sibling/child asset paths referenced by the share.
  try {
    const files = await pb.collection('workspace_files').getFullList({
      filter: pb.filter('workspace = {:ws}', { ws: wsPbId }),
      fields: 'path,storage_path',
    });
    const dir = itemPath.includes('/') ? itemPath.replace(/\/[^/]+$/, '') : '';
    for (const f of files) {
      const p = String(f.path ?? '');
      if (!f.storage_path) continue;
      const underItem = p === itemPath || p.startsWith(`${itemPath}/`);
      const underDir = dir ? p === dir || p.startsWith(`${dir}/`) : false;
      if (!underItem && !underDir) continue;
      try {
        await pb.collection('workspace_share_link_files').create({
          legacy_id: newLegacyId(),
          link: row.id,
          file_path: p,
        });
      } catch {
        /* ignore duplicates */
      }
    }
  } catch (e) {
    console.warn('share link file allowlist failed', e);
  }

  return mapShareLink(row, params.workspaceId);
}

export async function listShareLinks(
  workspaceId: string,
): Promise<WorkspaceShareLink[]> {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const rows = await pb.collection('workspace_share_links').getFullList({
    filter: pb.filter('workspace = {:ws}', { ws: wsPbId }),
    sort: '-created',
  });
  return rows
    .filter((r) => !r.revoked_at)
    .map((r) => mapShareLink(r, workspaceId));
}

async function resolveShareLinkRecord(linkId: string): Promise<RecordModel> {
  const pb = getPB();
  return pb
    .collection('workspace_share_links')
    .getFirstListItem(
      pb.filter('legacy_id = {:id} || id = {:id}', { id: linkId }),
    );
}

export async function refreshShareLink(linkId: string) {
  const pb = getPB();
  const link = await resolveShareLinkRecord(linkId);
  const wsPbId = String(link.workspace);
  const file = link.item_path
    ? await findFileRecord(wsPbId, String(link.item_path))
    : null;
  const patch: Record<string, unknown> = {
    refreshed_at: new Date().toISOString(),
  };
  if (file?.content != null) {
    patch.markdown_cache = file.content;
    patch.title_cache = titleFromPath(String(file.path));
  }
  const updated = await pb
    .collection('workspace_share_links')
    .update(link.id, patch);
  return mapShareLink(updated);
}

export async function revokeShareLink(linkId: string) {
  const pb = getPB();
  const link = await resolveShareLinkRecord(linkId);
  await pb.collection('workspace_share_links').update(link.id, {
    revoked_at: new Date().toISOString(),
  });
}

export async function getSharePayload(token: string) {
  const pb = getPB();
  const trimmed = token.trim();
  // Public list/view rules require ?share_token=<token> (see PocketBase ACL).
  const link = await pb
    .collection('workspace_share_links')
    .getFirstListItem(pb.filter('token = {:token}', { token: trimmed }), {
      query: { share_token: trimmed },
    });
  if (link.revoked_at) throw new Error('revoked');
  if (link.expires_at && new Date(pbDate(link.expires_at)) < new Date()) {
    throw new Error('expired');
  }
  let workspacePublicId = '';
  try {
    const ws = await pb.collection('workspaces').getOne(String(link.workspace));
    workspacePublicId = publicId(ws);
  } catch {
    workspacePublicId = String(link.workspace);
  }
  return {
    token: String(link.token),
    workspace_id: workspacePublicId,
    item_path: String(link.item_path ?? ''),
    title: String(link.title_cache ?? ''),
    markdown: String(link.markdown_cache ?? ''),
    allow_copy: Boolean(link.allow_copy),
    refreshed_at: pbDate(link.refreshed_at ?? link.updated),
  };
}

/**
 * Public URL for a reader-link asset. Served by the PocketBase hook
 * `pb_hooks/sariz-share-media.pb.js` (`GET /api/sariz/share-media`).
 *
 * `filePath` should be the workspace-relative path (resolve note-relative
 * Markdown hrefs against the share's `item_path` first).
 */
export function getShareMediaUrl(token: string, filePath: string): string {
  const base = getSupabaseUrl() || '';
  const params = new URLSearchParams({ token, path: filePath });
  return `${base}/api/sariz/share-media?${params.toString()}`;
}

export async function listWorkspaceFiles(
  workspaceId: string,
): Promise<WorkspaceFileRow[]> {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const rows = await pb.collection('workspace_files').getFullList({
    filter: pb.filter('workspace = {:ws}', { ws: wsPbId }),
    // Omit heavy content bodies for sidebar listing.
    fields:
      'id,legacy_id,workspace,path,storage_path,content_type,is_private,byte_size,content_hash,word_count,char_count,char_count_no_spaces,ctime,mtime,created,updated',
  });
  return rows.map((r) => ({
    ...mapFile(r, workspaceId),
    content: null,
  }));
}

export async function backfillMissingFileContentStats(
  workspaceId: string,
  rows: readonly Pick<
    WorkspaceFileRow,
    | 'path'
    | 'content_type'
    | 'word_count'
    | 'char_count'
    | 'char_count_no_spaces'
  >[],
): Promise<
  Map<
    string,
    { wordCount: number; charCount: number; charCountNoSpaces: number }
  >
> {
  const result = new Map<
    string,
    { wordCount: number; charCount: number; charCountNoSpaces: number }
  >();
  const missing: string[] = [];
  for (const row of rows) {
    if (
      typeof row.word_count === 'number' &&
      typeof row.char_count === 'number' &&
      typeof row.char_count_no_spaces === 'number'
    ) {
      result.set(row.path, {
        wordCount: row.word_count,
        charCount: row.char_count,
        charCountNoSpaces: row.char_count_no_spaces,
      });
      continue;
    }
    const type = row.content_type || '';
    if (
      type.includes('markdown') ||
      type.includes('text') ||
      row.path.endsWith('.md') ||
      row.path.endsWith('.markdown') ||
      row.path.endsWith('.txt')
    ) {
      missing.push(row.path);
    }
  }
  if (missing.length === 0) return result;

  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const CHUNK = 50;
  for (let i = 0; i < missing.length; i += CHUNK) {
    const chunk = missing.slice(i, i + CHUNK);
    for (const path of chunk) {
      const row = await findFileRecord(wsPbId, path);
      if (!row) continue;
      const stats = fileContentStats((row.content as string | null) ?? null);
      result.set(path, stats);
      await pb.collection('workspace_files').update(row.id, {
        word_count: stats.wordCount,
        char_count: stats.charCount,
        char_count_no_spaces: stats.charCountNoSpaces,
      });
    }
  }
  return result;
}

export async function getWorkspaceFile(
  workspaceId: string,
  path: string,
): Promise<WorkspaceFileRow | null> {
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const row = await findFileRecord(wsPbId, path);
  return row ? mapFile(row, workspaceId) : null;
}

function fileStatsFromRow(row: {
  content?: string | null;
  word_count?: number | null;
  char_count?: number | null;
  char_count_no_spaces?: number | null;
}): Record<string, number> {
  if (
    row.word_count != null &&
    row.char_count != null &&
    row.char_count_no_spaces != null
  ) {
    return {
      word_count: row.word_count,
      char_count: row.char_count,
      char_count_no_spaces: row.char_count_no_spaces,
    };
  }
  if (row.content != null) {
    const computed = fileContentStats(row.content);
    return {
      word_count: computed.wordCount,
      char_count: computed.charCount,
      char_count_no_spaces: computed.charCountNoSpaces,
    };
  }
  return {};
}

export async function upsertWorkspaceFile(row: {
  workspace_id: string;
  path: string;
  content?: string | null;
  storage_path?: string | null;
  content_type?: string;
  byte_size?: number;
  content_hash?: string | null;
  is_private?: boolean;
  word_count?: number | null;
  char_count?: number | null;
  char_count_no_spaces?: number | null;
}) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(row.workspace_id);
  const now = new Date().toISOString();
  const stats = fileStatsFromRow(row);
  const existing = await findFileRecord(wsPbId, row.path);
  const payload: Record<string, unknown> = {
    path: row.path,
    workspace: wsPbId,
    mtime: now,
    ...stats,
  };
  if ('content' in row) payload.content = row.content ?? null;
  if ('storage_path' in row) payload.storage_path = row.storage_path ?? null;
  if ('content_type' in row) payload.content_type = row.content_type;
  if ('byte_size' in row) payload.byte_size = row.byte_size;
  if ('content_hash' in row) payload.content_hash = row.content_hash ?? null;
  if ('is_private' in row) payload.is_private = row.is_private;

  if (existing) {
    const updated = await pb
      .collection('workspace_files')
      .update(existing.id, payload);
    return mapFile(updated, row.workspace_id);
  }
  const created = await pb.collection('workspace_files').create({
    legacy_id: newLegacyId(),
    ctime: now,
    content_type: row.content_type ?? 'text/markdown',
    byte_size: row.byte_size ?? 0,
    is_private: row.is_private ?? false,
    ...payload,
  });
  return mapFile(created, row.workspace_id);
}

export async function insertWorkspaceFile(row: {
  workspace_id: string;
  path: string;
  content?: string | null;
  storage_path?: string | null;
  content_type?: string;
  byte_size?: number;
  content_hash?: string | null;
  is_private?: boolean;
  word_count?: number | null;
  char_count?: number | null;
  char_count_no_spaces?: number | null;
}) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(row.workspace_id);
  const now = new Date().toISOString();
  const stats = fileStatsFromRow(row);
  const created = await pb.collection('workspace_files').create({
    legacy_id: newLegacyId(),
    workspace: wsPbId,
    path: row.path,
    content: row.content ?? null,
    storage_path: row.storage_path ?? null,
    content_type: row.content_type ?? 'text/markdown',
    byte_size: row.byte_size ?? 0,
    content_hash: row.content_hash ?? null,
    is_private: row.is_private ?? false,
    ctime: now,
    mtime: now,
    ...stats,
  });
  return mapFile(created, row.workspace_id);
}

export async function updateWorkspaceFileContent(
  workspaceId: string,
  path: string,
  patch: Partial<
    Pick<
      WorkspaceFileRow,
      | 'content'
      | 'storage_path'
      | 'content_type'
      | 'byte_size'
      | 'content_hash'
      | 'word_count'
      | 'char_count'
      | 'char_count_no_spaces'
    >
  >,
) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const existing = await findFileRecord(wsPbId, path);
  if (!existing) throw new Error('file not found');
  const statsPatch =
    patch.word_count != null ||
    patch.char_count != null ||
    patch.char_count_no_spaces != null ||
    patch.content === undefined
      ? {}
      : (() => {
          const computed = fileContentStats(patch.content);
          return {
            word_count: computed.wordCount,
            char_count: computed.charCount,
            char_count_no_spaces: computed.charCountNoSpaces,
          };
        })();
  const updated = await pb.collection('workspace_files').update(existing.id, {
    ...patch,
    ...statsPatch,
    mtime: new Date().toISOString(),
  });
  return mapFile(updated, workspaceId);
}

/** Thrown when a durable save path is unavailable (kept for caller compatibility). */
export class SaveWorkspaceRpcMissingError extends Error {
  constructor() {
    super('save_workspace_file RPC is not deployed');
    this.name = 'SaveWorkspaceRpcMissingError';
  }
}

/**
 * Durable save: update the file row and refresh active reader-link caches.
 */
export async function saveWorkspaceFileContent(params: {
  workspaceId: string;
  path: string;
  content: string | null;
  storagePath: string | null;
  contentType: string;
  byteSize: number;
  wordCount?: number | null;
  charCount?: number | null;
  charCountNoSpaces?: number | null;
}): Promise<WorkspaceFileRow | null> {
  const stats =
    params.wordCount != null &&
    params.charCount != null &&
    params.charCountNoSpaces != null
      ? {
          word_count: params.wordCount,
          char_count: params.charCount,
          char_count_no_spaces: params.charCountNoSpaces,
        }
      : params.content != null
        ? (() => {
            const computed = fileContentStats(params.content);
            return {
              word_count: computed.wordCount,
              char_count: computed.charCount,
              char_count_no_spaces: computed.charCountNoSpaces,
            };
          })()
        : {
            word_count: null,
            char_count: null,
            char_count_no_spaces: null,
          };

  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  const existing = await findFileRecord(wsPbId, params.path);
  if (!existing) return null;

  const updated = await pb.collection('workspace_files').update(existing.id, {
    content: params.content,
    storage_path: params.storagePath,
    content_type: params.contentType,
    byte_size: params.byteSize,
    mtime: new Date().toISOString(),
    ...stats,
  });

  // Refresh active share-link caches for this path.
  try {
    const links = await pb.collection('workspace_share_links').getFullList({
      filter: pb.filter('workspace = {:ws} && item_path = {:path}', {
        ws: wsPbId,
        path: params.path,
      }),
    });
    const title = titleFromPath(params.path);
    for (const link of links) {
      if (link.revoked_at) continue;
      await pb.collection('workspace_share_links').update(link.id, {
        markdown_cache: params.content ?? '',
        title_cache: title,
        refreshed_at: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.warn('share link cache refresh failed', e);
  }

  return mapFile(updated, params.workspaceId);
}

export async function deleteWorkspaceFile(workspaceId: string, path: string) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const existing = await findFileRecord(wsPbId, path);
  if (!existing) return;
  await pb.collection('workspace_files').delete(existing.id);
}

export async function renameWorkspaceFile(
  workspaceId: string,
  oldPath: string,
  newPath: string,
) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  const existing = await findFileRecord(wsPbId, oldPath);
  if (!existing) throw new Error('file not found');
  const updated = await pb.collection('workspace_files').update(existing.id, {
    path: newPath,
    mtime: new Date().toISOString(),
  });
  return mapFile(updated, workspaceId);
}

// ---------------------------------------------------------------------------
// Assets / avatars
// ---------------------------------------------------------------------------

export async function uploadAsset(
  workspaceId: string,
  relativePath: string,
  file: File,
): Promise<string> {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);
  // note_assets has storage_path (unique) + file — no separate `path` field.
  const storagePath = `${workspaceId}/${relativePath}`;

  try {
    const existing = await pb.collection('note_assets').getFirstListItem(
      pb.filter('storage_path = {:sp}', { sp: storagePath }),
    );
    const updateForm = new FormData();
    updateForm.append('file', file);
    await pb.collection('note_assets').update(existing.id, updateForm);
  } catch {
    const createForm = new FormData();
    createForm.append('legacy_id', newLegacyId());
    createForm.append('workspace', wsPbId);
    createForm.append('storage_path', storagePath);
    createForm.append('file', file);
    await pb.collection('note_assets').create(createForm);
  }
  return storagePath;
}

const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]);

function avatarObjectPathFromPublicUrl(
  avatarUrl: string | null,
): string | null {
  if (!avatarUrl) return null;
  try {
    const parsed = new URL(avatarUrl);
    // PocketBase file URLs: /api/files/<collectionId>/<recordId>/<filename>
    const marker = '/api/files/';
    const idx = parsed.pathname.indexOf(marker);
    if (idx === -1) return null;
    return decodeURIComponent(parsed.pathname.slice(idx + marker.length));
  } catch {
    return null;
  }
}

export function isAllowedAvatarFile(file: File):
  | {
      ok: true;
    }
  | {
      ok: false;
      reason: 'type' | 'size';
    } {
  if (!AVATAR_MIME_TYPES.has(file.type)) {
    return { ok: false, reason: 'type' };
  }
  if (file.size > AVATAR_MAX_BYTES) {
    return { ok: false, reason: 'size' };
  }
  return { ok: true };
}

export async function uploadAvatar(file: File): Promise<string> {
  const user = await getUser();
  if (!user) throw new Error('Not signed in');
  const check = isAllowedAvatarFile(file);
  if (!check.ok) {
    throw new Error(
      check.reason === 'type'
        ? 'Unsupported avatar image type'
        : 'Avatar image is too large',
    );
  }
  const pb = getPB();
  const form = new FormData();
  form.append('avatar', file);

  // Prefer updating the auth user record's avatar file field when present;
  // also mirror into profiles.avatar_url for app consumers.
  let publicUrl = '';
  try {
    const updated = await pb.collection('users').update(user.id, form);
    if (updated.avatar) {
      publicUrl = pb.files.getURL(updated, updated.avatar as string);
    }
  } catch (e) {
    console.warn('user avatar upload failed; using profile only', e);
  }

  if (!publicUrl) {
    // Fallback: store URL-less marker; profile update still records metadata.
    publicUrl = `${getSupabaseUrl() || ''}/api/files/users/${user.id}/avatar`;
  }
  const busted = `${publicUrl}?t=${Date.now()}`;
  await updateMyProfile({ avatar_url: busted });
  return busted;
}

export async function removeAvatarObject(
  avatarUrl: string | null,
): Promise<void> {
  const path = avatarObjectPathFromPublicUrl(avatarUrl);
  if (!path) return;
  // TODO: delete PocketBase file field on users/profiles when rules allow.
  void path;
}

export async function downloadAsset(storagePath: string): Promise<Blob> {
  const pb = getPB();
  // storagePath format: `{workspacePublicId}/{relativePath}` — unique on note_assets.
  const asset = await pb.collection('note_assets').getFirstListItem(
    pb.filter('storage_path = {:sp}', { sp: storagePath }),
  );
  const filename = asset.file as string;
  if (!filename) throw new Error('asset has no file');
  const url = pb.files.getURL(asset, filename);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`asset download failed: ${res.status}`);
  return res.blob();
}

export function subscribeWorkspaceFiles(
  workspaceId: string,
  onChange: (payload: {
    eventType: string;
    path?: string;
    oldPath?: string;
  }) => void,
): () => void {
  const pb = getPB();
  let unsub: (() => void) | undefined;
  let cancelled = false;

  void (async () => {
    try {
      const wsPbId = await resolveWorkspacePbId(workspaceId);
      if (cancelled) return;
      unsub = await pb.collection('workspace_files').subscribe(
        '*',
        (e) => {
          const record = e.record as RecordModel | undefined;
          const path = record ? String(record.path ?? '') : undefined;
          const eventType =
            e.action === 'create'
              ? 'INSERT'
              : e.action === 'update'
                ? 'UPDATE'
                : e.action === 'delete'
                  ? 'DELETE'
                  : String(e.action).toUpperCase();
          onChange({ eventType, path, oldPath: path });
        },
        { filter: `workspace = "${wsPbId}"` },
      );
    } catch (err) {
      console.warn('workspace_files subscribe failed', err);
    }
  })();

  return () => {
    cancelled = true;
    void unsub?.();
  };
}

export type {
  CommentMention,
  MentionCandidate,
  MentionTextSegment,
} from './mentions';
export {
  filterMentionCandidates,
  mentionLabel,
  mentionsStillInBody,
  mentionToken,
  normalizeMentionCandidate,
  personLabel,
  readOpenMention,
  renderMentionBody,
  segmentMentionText,
} from './mentions';
export type {
  WorkspacePresenceLocalState,
  WorkspacePresencePeer,
} from './workspace-presence';
export {
  getActiveWorkspacePresenceId,
  joinWorkspacePresence,
  leaveWorkspacePresence,
  pickPresenceColor,
  updateWorkspacePresenceLocal,
} from './workspace-presence';
export type { CommentReply, CommentThread } from './yjs/comments';
export {
  createComment,
  deleteComment,
  getCommentsState,
  observeComments,
  replyToComment,
  setCommentResolved,
} from './yjs/comments';
export {
  archiveFileYdocVersion,
  collabPathKey,
  deleteFileYdoc,
  insertFileYdocUpdate,
  isCollabActive,
  loadFileYdocSnapshot,
  markCollabActive,
  markCollabInactive,
  renameFileYdoc,
  upsertFileYdocSnapshot,
} from './yjs/db';
export {
  clearLocalFileYdoc,
  clearLocalWorkspaceYdocs,
  readLocalFileYdoc,
  writeLocalFileYdoc,
} from './yjs/local';
export type {
  CollabPresencePeer,
  CollabStatus,
  CollabUser,
} from './yjs/provider';
export { SupabaseFileYjsProvider } from './yjs/provider';
